import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { Loader } from 'lucide-react'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useResetPassword } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput.tsx'
import { useToast } from '@/components/toast/useToast.ts'

import { mapProblemDetailErrors, passwordSchema } from '@/utils/validation.ts'

const resetPasswordFormSchema = z.intersection(
    passwordSchema,
    z.object({
        email: z.string().email(),
    }),
)

type ResetPasswordFormSchema = z.infer<typeof resetPasswordFormSchema>

interface ResetPasswordFormProps extends React.HTMLAttributes<HTMLDivElement> {
    forgotPasswordToken: string
}

export default function ResetPasswordForm({ forgotPasswordToken }: ResetPasswordFormProps) {
    const { toast } = useToast()
    const navigate = useNavigate()
    const resetPasswordMutator = useResetPassword()

    const form = useForm<ResetPasswordFormSchema>({
        resolver: zodResolver(resetPasswordFormSchema),
        mode: 'onBlur',
    })

    const onSubmit = (values: ResetPasswordFormSchema) => {
        const postValues = {
            ...values,
            forgotPasswordToken,
        }
        resetPasswordMutator.mutate(postValues, {
            onSuccess: () => {
                toast({
                    variant: 'success',
                    title: 'Password reset email sent!',
                    description: 'Your password has been reset, you can now login using your new password.',
                })
                navigate({
                    to: '/',
                    search: {
                        welcome: true,
                    },
                })
            },
            // @ts-expect-error - Kubb generated code is not typed correctly
            onError: (error: AxiosError) => {
                mapProblemDetailErrors(error, form.formState.errors)
                toast({
                    variant: 'destructive',
                    title: 'Reset password failed!',
                    description: `Failed to reset password, please try again`,
                })
            },
        })
    }

    return (
        <>
            <div className="flex flex-col space-y-2 text-center">
                <h1 className="text-2xl font-semibold tracking-tight">Reset password</h1>
            </div>
            <p className="mb-2 block text-center">Use the form below to reset your password.</p>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                    <FormInput
                        control={form.control}
                        name="email"
                        label="Email"
                        className="text-left"
                        autoComplete="email"
                    />
                    <FormInput
                        control={form.control}
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        className="text-left"
                        label="Password"
                    />
                    <FormInput
                        control={form.control}
                        name="passwordConfirm"
                        type="password"
                        autoComplete="new-password"
                        className="text-left"
                        label="Confirm password"
                    />
                    <Button type="submit" disabled={resetPasswordMutator.isPending}>
                        {resetPasswordMutator.isPending && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                        Reset Password
                    </Button>
                </form>
            </Form>
        </>
    )
}
